/**
 * PF Revamp — pure `PfStoreState -> PaymentIntentProductV2DTO[]` serializer.
 *
 * Sibling of `serializePlacement`, and deliberately NOT the same list. The two
 * requests price through opposite halves of the backend:
 *
 *   - **Placement** (`POST /workspace/{ws}/orders`) hands the BE the client's
 *     intent — `organizationMethod`, `fastDelivery`, `billingMethod`,
 *     `selectedFavouriteCreativeId` — and `FeeProductDeriver` appends the
 *     ORGANIZATION / travel-cost / DELIVERY / BILLING product lines itself.
 *     Sending them from here would double-charge, so `serializePlacement`
 *     omits them by design.
 *   - **The payment intent** derives nothing: `SessionPaymentService
 *     .calculateAmount` is `Σ(unit net price × count)` − discount + VAT over the
 *     lines it is given. A fee that is not in this list is a fee Stripe never
 *     collects while the order still bills it, and nothing reconciles the gap.
 *
 * So the rule for this file is: **every chargeable line the order will end up
 * carrying**. Cart variants, their addons and item extras come from the same
 * helpers placement uses (`findVariant` / `buildAddons`), so the product half
 * can never drift; the fee half comes from `extrasFromInstructions`, which is
 * the snapshot the cart total is built from — one source for what the client
 * was quoted and what the client is charged.
 *
 * Kept pure (no logging / no I/O), like `serializePlacement`: the hook owns the
 * precondition reporting.
 *
 * KNOWN GAP — the BILLING fee. `FeeProductDeriver` also derives a line from
 * `billingMethod`, and it is not in `extrasFromInstructions`, so it is not sent
 * here. Every BILLING instruction option observed on dev prices at **0.00**
 * (CARDPAYMENT / SEPADIRECTDEBIT / INVOICEBYEMAIL), which is why this is a gap
 * and not a live undercharge — but a country that ever prices one would be
 * charged short. Registered as DF-B12; the fix is to mirror a priced billing
 * option into the snapshot list, where it also belongs on screen.
 */

import type { CartItemId, PaymentIntentProductV2DTO, ResolvedCatalogue } from 'models/purchaseFlowRevamp'

import type { PfStoreState } from './store'
import { buildAddons, findVariant } from './serializeLine'
import { orderExtras } from './stagingRenovation'

/**
 * The lines the v2 payment intent must charge for.
 *
 * Returns `null` when there is no session, or when the cart resolves to no
 * line at all — an empty `products` array is a 422 on the wire, so refusing to
 * build one lets the caller report the state instead of firing a doomed request.
 *
 * Duplicate codes are left as separate lines on purpose: the BE merges them
 * (`ProductQuantityDTO.mergeById`) and keeping them positional matches how
 * placement sends two lines for the same product bought twice.
 *
 * @example
 * buildPaymentIntentBody(state, catalogue)
 * // [{ productCode: 'p_variant_1', count: 2 }, { productCode: 'KEYPICKUP', count: 1 }]
 */
export function buildPaymentIntentBody(
  state: PfStoreState,
  catalogue: ResolvedCatalogue | undefined
): PaymentIntentProductV2DTO[] | null {
  return buildPricedLines(state, catalogue)?.lines ?? null
}

/**
 * The payment intent's lines, each paired with the cart item it prices.
 *
 * `owners[i]` is the cart item behind `lines[i]` (its variant or one of its add-ons), or `null` for a
 * fee mirrored from the instructions step. `POST …/sessions/{id}/pricing` answers one entry per request
 * line in request order, so this pairing is what lets a priced line find its way back onto the cart.
 */
export function buildPricedLines(
  state: PfStoreState,
  catalogue: ResolvedCatalogue | undefined
): { lines: PaymentIntentProductV2DTO[]; owners: (CartItemId | null)[] } | null {
  if (!state.draft.sessionId) return null

  const lines: PaymentIntentProductV2DTO[] = []
  const owners: (CartItemId | null)[] = []

  for (const item of state.items) {
    const variant = findVariant(catalogue, item.variantId)
    // Charging for fewer lines than the cart shows is never right, so no intent is built at all.
    if (!variant) return null

    lines.push({ productCode: variant.productCode, count: item.quantity })
    owners.push(item.id)
    for (const addon of buildAddons(item, variant, catalogue)) {
      lines.push({ productCode: addon.productCode, count: addon.count })
      owners.push(item.id)
    }
  }

  if (lines.length === 0) return null

  // The fees the order will derive server-side. A snapshot without a
  // `productCode` is display-only — there is no code to charge, and inventing
  // one would fail the whole request, so it is skipped rather than guessed.
  for (const extra of orderExtras(state, catalogue)) {
    if (extra.productCode) {
      lines.push({ productCode: extra.productCode, count: extra.count ?? 1 })
      owners.push(null)
    }
    // The implied fee is charged but never quoted: its amount is not on the
    // wire, so it can reach the charge without reaching the cart total.
    if (extra.impliedFeeProductCode) {
      lines.push({ productCode: extra.impliedFeeProductCode, count: 1 })
      owners.push(null)
    }
  }

  return { lines, owners }
}
